import type { FullPokemonCard } from '@/types/pokemon'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

type ScanCardAlternativesProps = {
  possibleCards: FullPokemonCard[]
  selectedCard?: FullPokemonCard
  selectedCardId?: string
  onSelectCard: (card: FullPokemonCard) => void
  compact?: boolean
  loading?: boolean
}

const SKELETON_COUNT = 4

const ScanCardAlternatives = ({ possibleCards, selectedCard, selectedCardId, onSelectCard, compact = false, loading = false }: ScanCardAlternativesProps) => {
  if (!loading && possibleCards.length === 0 && !selectedCard) {
    return null
  }

  const renderSkeletonCard = (index: number) => (
    <div
      key={`skeleton-${index}`}
      className={`flex flex-col items-center rounded-xl shrink-0 border border-gray-200 bg-white ${compact ? 'p-2 w-[120px] gap-1.5' : 'p-4 w-[200px] gap-3'}`}
    >
      <Skeleton className="w-full aspect-63/88 rounded-lg" />
      <div className={`w-full flex flex-col ${compact ? 'gap-1' : 'gap-2'}`}>
        <Skeleton className={`h-4 ${compact ? 'w-3/4' : 'w-full'}`} />
        <Skeleton className={`h-3 ${compact ? 'w-1/2' : 'w-2/3'}`} />
      </div>
    </div>
  )

  const renderCard = (card: FullPokemonCard, isSelected: boolean = false) => {
    return (
      <button
        key={card.id}
        onClick={() => !isSelected && onSelectCard(card)}
        className={`flex flex-col items-center gap-3 rounded-xl transition-all duration-200 shrink-0 ${compact ? 'p-2 w-[120px] gap-1.5' : 'p-4 w-[200px] gap-3'} ${
          isSelected
            ? 'bg-blue-50 border-2 border-blue-600 shadow-md cursor-default'
            : 'bg-white border border-gray-200 hover:bg-gray-50 hover:border-blue-400 hover:shadow-md cursor-pointer'
        }`}
        disabled={isSelected}
      >
        {/* Card Image */}
        <div className="w-full aspect-63/88 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center">
          {card.cardData.imageUrl ? (
            <img
              src={card.cardData.imageUrl}
              alt={card.name}
              className="w-full h-full object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
              }}
            />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-400"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          )}
        </div>

        {/* Card Info */}
        <div className={`w-full flex flex-col text-left ${compact ? 'gap-1' : 'gap-2'}`}>
          <div className="flex items-center gap-2">
            <p className={`font-semibold m-0 truncate ${compact ? 'text-xs' : 'text-sm'} ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{card.name}</p>
            {isSelected && !compact && <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded font-medium shrink-0">Selected</span>}
          </div>
          <div className={`flex flex-col text-gray-600 ${compact ? 'gap-0.5 text-[10px]' : 'gap-1.5 text-xs'}`}>
            {card.set && (
              <div className="flex items-center gap-1.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-500 shrink-0"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                </svg>
                <span className="truncate">{card.set.name}</span>
              </div>
            )}
            {card.collectorNumber && (
              <div className="flex items-center gap-1.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-500 shrink-0"
                >
                  <line x1="4" y1="9" x2="20" y2="9" />
                  <line x1="4" y1="15" x2="20" y2="15" />
                  <line x1="10" y1="3" x2="8" y2="21" />
                  <line x1="16" y1="3" x2="14" y2="21" />
                </svg>
                <span>#{card.collectorNumber}</span>
              </div>
            )}
            {card.cardMarketPrice == null && (
              <Badge
                variant="outline"
                title="No Cardmarket price data"
                className={cn('mt-0.5 border-amber-300 bg-amber-50 text-amber-900 font-normal', compact && 'text-[9px] px-1 py-0 leading-tight max-w-full truncate')}
              >
                {compact ? 'No price' : 'No Cardmarket price'}
              </Badge>
            )}
          </div>
        </div>
      </button>
    )
  }

  return (
    <>
      <style>{`
        .scan-card-alternatives-scroll {
          scrollbar-width: thin;
          scrollbar-color: #d1d5db #f3f4f6;
        }
        .scan-card-alternatives-scroll::-webkit-scrollbar {
          height: 8px;
        }
        .scan-card-alternatives-scroll::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 4px;
        }
        .scan-card-alternatives-scroll::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 4px;
        }
        .scan-card-alternatives-scroll::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>

      <div className={`overflow-hidden shadow-sm w-full ${compact ? 'mt-2' : 'mt-2'}`}>
        <div className={compact ? 'p-2' : 'p-4'}>
          <div className="flex gap-4 overflow-x-auto pb-2 scan-card-alternatives-scroll">
            {loading ? (
              Array.from({ length: SKELETON_COUNT }).map((_, i) => renderSkeletonCard(i))
            ) : (
              <>
                {selectedCard && renderCard(selectedCard, true)}
                {possibleCards.filter((card) => card.id !== selectedCardId).map((card) => renderCard(card, false))}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ScanCardAlternatives
